import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress,
  Avatar,
  TextField,
  InputAdornment,
  Divider,
  Link,
  Chip,
  IconButton,
} from "@mui/material";
import {
  Check as CheckIcon,
  Search as SearchIcon,
  Share as ShareIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { useSelector, useDispatch } from "react-redux";
import store from "src/redux/store";
import {
  setIsShowSetupMenu,
  setShowSubscriptionPlanDialog,
  setShowPaymentSuccessDialog,
  setShowPaymentErrorDialog,
  setPaymentErrorMessage,
  setLastSubscriptionBusinessId,
  switchProfile,
} from "src/redux/slices/AuthSlice";
import { fetchUser } from "src/redux/actions/AuthAction";
import authConfig from "src/configs/auth";
import NameLogo from "./icons/Name-Logo";
import toast from "react-hot-toast";
import DowngradePlanDialog from "./DowngradePlanDialog";
import SubscriptionAgreementDialog from "./SubscriptionAgreementDialog";

// custom hook to fetch subscription plans
const useSubscriptionPlans = (enabled = true) => {
  return useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        ApiEndPoints.BUSINESS.subscriptionPlansList
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: enabled,
  });
};

// component to format feature key by replacing underscores with spaces and capitalizing
const formatFeatureKey = (featureKey) => {
  return featureKey.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

// Numeral digit maps for Indic scripts
const NUMERAL_MAPS = {
  kn: ['೦','೧','೨','೩','೪','೫','೬','೭','೮','೯'],
  hi: ['०','१','२','३','४','५','६','७','८','९'],
  mr: ['०','१','२','३','४','५','६','७','८','९'],
  gu: ['૦','૧','૨','૩','૪','૫','૬','૭','૮','૯'],
  bn: ['০','১','২','৩','৪','৫','৬','৭','৮','৯'],
  te: ['౦','౧','౨','౩','౪','౫','౬','౭','౮','౯'],
};

// Convert a number or string of digits to the locale's numeral system
const convertNumerals = (value, lang) => {
  const map = NUMERAL_MAPS[lang];
  if (!map) return String(value);
  return String(value).replace(/\d/g, (d) => map[parseInt(d, 10)]);
};

// custom hook to fetch subscription info (exported for SetupDialog to share cache)
export const useSubscriptionInfo = (businessId, enabled = true) => {
  return useQuery({
    queryKey: ["subscription-info", businessId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        ApiEndPoints.BUSINESS.getBusinessInfoAboutSubscription(businessId)
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: enabled && !!businessId,
  });
};

// custom hook to fetch subscription invoices
const useSubscriptionInvoices = (businessId, enabled = true) => {
  return useQuery({
    queryKey: ["subscription-invoices", businessId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        ApiEndPoints.BUSINESS.getSubscriptionInvoices(businessId)
      );
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: enabled && !!businessId,
    retry: 1,
  });
};

// Format date to DD.MM.YYYY format
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

// Format date to HH:MM, MM/DD/YYYY format
const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${hours}:${minutes}, ${month}/${day}/${year}`;
};

// Subscription Management View Component
const SubscriptionManagementView = ({
  subscriptionInfo,
  invoices,
  invoicesLoading,
  invoicesError,
  onUpgrade,
  onSwitchPackage,
  IsMaxView = false,
  businessId,
  subscriptionStatus,
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const tPlanName = (name) => name ? t(`plan_${name.toLowerCase().replace(/ /g, '_')}`, { defaultValue: name }) : name;
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();
  const paymentWindowIntervalRef = useRef(null);

  const invoiceList = invoices || [];

  const filteredInvoices = invoiceList.filter((invoice) => {
    const searchValue = searchTerm.trim().toLowerCase();
    if (!searchValue) return true;

    const planName = invoice?.metadata?.planName || "";
    const status = invoice?.status || "";
    const paymentMethod = invoice?.paymentMethod || "";
    const transactionId = invoice?.transactionId || "";

    return (
      planName.toLowerCase().includes(searchValue) ||
      status.toLowerCase().includes(searchValue) ||
      paymentMethod.toLowerCase().includes(searchValue) ||
      transactionId.toLowerCase().includes(searchValue)
    );
  });

  const planName = subscriptionInfo?.subscriptionPlan?.planName || "";
  const pricePerMonth = subscriptionInfo?.subscriptionPlan?.pricePerMonth || 0;
  const endDate = subscriptionInfo?.subScriptionEndDate;
  const status = subscriptionStatus || subscriptionInfo?.subscriptionStatus || "";
  const normalizedStatus =
    typeof status === "string"
      ? status.toLowerCase()
      : "";
  const isSuspended = normalizedStatus === "suspended";
  const isScheduledDowngrade = subscriptionInfo?.isScheduledDowngrade || false;
  const cancelAtPeriodEnd = subscriptionInfo?.cancelAtPeriodEnd || false;
  const scheduledPlanName = subscriptionInfo?.scheduledPlanName || "";
  const scheduledDowngradeDate = subscriptionInfo?.scheduledDowngradeDate;

  // Handler to download invoice PDF
  const handleDownloadInvoice = (invoicePdfUrl) => {
    if (invoicePdfUrl) {
      window.open(invoicePdfUrl, "_blank");
    }
  };

  // Handler to pay failed invoice
  const handlePayInvoice = (hostedInvoiceUrl) => {
    if (hostedInvoiceUrl) {
      // Clear any existing interval
      if (paymentWindowIntervalRef.current) {
        clearInterval(paymentWindowIntervalRef.current);
        paymentWindowIntervalRef.current = null;
      }

      const width = 600;
      const height = 800;
      const windowLeft = window.screenLeft || window.screenX || 0;
      const windowTop = window.screenTop || window.screenY || 0;
      const windowWidth = window.outerWidth || window.innerWidth;
      const windowHeight = window.outerHeight || window.innerHeight;
      const left = windowLeft + (windowWidth - width) / 2;
      const top = windowTop + (windowHeight - height) / 2;

      // Store the popup reference
      const paymentWindow = window.open(
        hostedInvoiceUrl,
        "invoice-payment",
        `width=${width},height=${height},left=${Math.round(
          left
        )},top=${Math.round(top)},scrollbars=yes,resizable=yes`
      );

      // Check if popup is closed every 500ms
      paymentWindowIntervalRef.current = setInterval(() => {
        if (!paymentWindow || paymentWindow.closed) {
          if (paymentWindowIntervalRef.current) {
            clearInterval(paymentWindowIntervalRef.current);
            paymentWindowIntervalRef.current = null;
          }
          // Popup closed - refresh both subscription info and invoices
          if (businessId) {
            queryClient.invalidateQueries(["subscription-info", businessId]);
            queryClient.invalidateQueries(["subscription-invoices", businessId]);
          }
        }
      }, 500);
    }
  };

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (paymentWindowIntervalRef.current) {
        clearInterval(paymentWindowIntervalRef.current);
        paymentWindowIntervalRef.current = null;
      }
    };
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: IsMaxView ? "100%" : "600px",
        mx: "auto",
        p: IsMaxView ? 2 : 0,
        height: IsMaxView ? "auto" : "80vh",
        display: "flex",
        flexDirection: "column",
        overflow: IsMaxView ? "visible" : "auto",
        backgroundColor: "#fff",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          textAlign: "center",
          p: 4,
          backgroundColor: "white",
          flexShrink: 0,
        }}
      >
        {/* Logo left, name + tagline right */}
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1.5, mb: 2, justifyContent: "center" }}>
          <Avatar
            src="/logo.svg"
            sx={{
              width: 70,
              height: 70,
              bgcolor: "#fff",
              border: "5px solid #A259FF",
              flexShrink: 0,
              padding: 3,
            }}
          >
            <NameLogo />
          </Avatar>
          <Box sx={{ textAlign: "left" }}>
            <Typography
             variant="h4"
             fontWeight="bold"
             letterSpacing={0.5}
             sx={{
               textTransform: "lowercase",
               textAlign: "center",
               color: "#000",
               fontFamily: "Inter, Arial, sans-serif",
               lineHeight: 1.1,
               fontSize: { xs: "1.5rem", sm: "2.5rem" },
              }}
            >
              be
              <Box component="span" sx={{ color: "#A259FF" }}>
                ɘ
              </Box>
              mz
            </Typography>
            <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ lineHeight: 1.2, textTransform: "lowercase" }}>
              {t('tagline')}
            </Typography>
          </Box>
        </Box>

        {/* Manage Subscription Link */}
        <Link
          component="button"
          variant="body1"
          fontWeight="bold"
          sx={{
            color: "#6200EE",
            textDecoration: "none",
            cursor: "pointer",
            mb: 2,
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {t('manageSubscription')}
        </Link>

        {/* Package Selected */}
        <Typography variant="body1" fontWeight="bold" color="text.secondary" sx={{ mb: 1, textTransform: "lowercase", fontSize: "0.95rem" }}>
          {t('packageSelected')}{" "}
          <Box
            component="span"
            sx={{
              color: "#6200EE",
              textDecoration: "underline",
              fontWeight: "bold",
              textTransform: "lowercase",
            }}
          >
            {planName}
          </Box>
        </Typography>

        {/* Switch Package Link */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={onSwitchPackage}
            disabled={isSuspended || isScheduledDowngrade}
            sx={{
              color: (isSuspended || isScheduledDowngrade) ? "#BDBDBD" : "#478AFF",
              textDecoration: "none",
              cursor: (isSuspended || isScheduledDowngrade) ? "not-allowed" : "pointer",
              fontSize: "0.875rem",
              opacity: (isSuspended || isScheduledDowngrade) ? 0.5 : 1,
              pointerEvents: (isSuspended || isScheduledDowngrade) ? "none" : "auto",
              "&:hover": {
                textDecoration: (isSuspended || isScheduledDowngrade) ? "none" : "underline",
              },
            }}
          >
            {t('switchPackage')}
          </Link>
          <InfoIcon sx={{ fontSize: 16, color: "#478AFF" }} />
        </Box>
      </Box>

      {/* Pricing Box */}
      <Box
        sx={{
          mx: 2,
          mb: 2,
          p: 2,
          border: "2px solid #A259FF",
          borderRadius: 2,
          backgroundColor: "white",
          textAlign: "center",
        }}
      >
        <Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5, fontSize: "1.1rem" }}>
          $ {convertNumerals(pricePerMonth, i18n.language)}/{t('month')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textTransform: "lowercase" }}>
          {t('billedMonthly')}
        </Typography>
      </Box>

      {/* Trial/Billing Status Notice */}
      <Box sx={{ mx: 2, mb: 3 }}>
        {cancelAtPeriodEnd ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "#FFF3E0",
              border: "1px solid #FFB74D",
            }}
          >
            <Typography
              variant="body2"
              align="center"
              sx={{ fontWeight: "bold", color: "#E65100" }}
            >
              {t('cancelledSubscriptionPlan')}
            </Typography>
            <Typography variant="body2" align="center" sx={{ color: "#E65100", mt: 0.5 }}>
              {t('planDowngradedFrom', {
                date: endDate ? formatDate(endDate) : t('theNextBillingCycle'),
              })}
            </Typography>
            <Typography variant="body2" align="center" sx={{ color: "#E65100", mt: 1, fontSize: "0.8rem" }}>
              {t('dataStoredNote')}
            </Typography>
          </Box>
        ) : isScheduledDowngrade && scheduledPlanName ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "#FFF3E0",
              border: "1px solid #FFB74D",
            }}
          >
            <Typography
              variant="body2"
              align="center"
              sx={{ fontWeight: "bold", color: "#E65100" }}
            >
              {t('planDowngradeScheduled')}
            </Typography>
            <Typography variant="body2" align="center" sx={{ color: "#E65100" }}>
              {t('currentPlanScheduled', {
                planName: tPlanName(planName),
                scheduledPlanName: tPlanName(scheduledPlanName),
                date: scheduledDowngradeDate ? formatDate(scheduledDowngradeDate) : t('theNextBillingCycle'),
              })}
            </Typography>
          </Box>
        ) : normalizedStatus === "trial" ? (
          endDate ? (
            <Typography variant="body2" align="center">
              {t('trialPeriodEndsOn', { date: formatDate(endDate) })}
            </Typography>
          ) : (
            <Typography variant="body2" align="center">
              {t('trialPeriodActive')}
            </Typography>
          )
        ) : normalizedStatus === "suspended" ? (
          <>
            <Typography
              variant="body2"
              align="center"
              color="error"
              sx={{ mb: 1, fontWeight: "bold" }}
            >
              {t('autoRenewalPaymentFailed')}
            </Typography>
            {endDate && (
              <Typography variant="body2" align="center">
                {t('subscriptionEndsOn', { date: formatDate(endDate) })}
              </Typography>
            )}
          </>
        ) : (
          endDate && (
            <Typography variant="body2" align="center">
              {t('nextBillingCycleStartsOn', { date: formatDate(endDate) })}
            </Typography>
          )
        )}
      </Box>
      {/* Upgrade Button */}
      <Box sx={{ mx: 2, mb: 3 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={onUpgrade}
          disabled={isSuspended || isScheduledDowngrade}
          sx={{
            backgroundColor: (isSuspended || isScheduledDowngrade) ? "#E0E0E0" : "#9347FF",
            color: (isSuspended || isScheduledDowngrade) ? "#BDBDBD" : "#FFFFFF",
            borderRadius: 2,
            py: 1.5,
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: "bold",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            cursor: (isSuspended || isScheduledDowngrade) ? "not-allowed" : "pointer",
            "&:hover": {
              backgroundColor: (isSuspended || isScheduledDowngrade) ? "#E0E0E0" : "#8938e9",
              boxShadow: (isSuspended || isScheduledDowngrade) ? "0 2px 4px rgba(0,0,0,0.1)" : "0 4px 8px rgba(0,0,0,0.15)",
            },
            "&:disabled": {
              backgroundColor: "#E0E0E0",
              color: "#BDBDBD",
            },
          }}
        >
          {t('upgradeButton')}
        </Button>
      </Box>

      {/* Invoice History Section */}
      <Box sx={{ mx: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t('invoiceHistory')}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              placeholder={t('searchInvoices')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9e9e9e", fontSize: "1.2rem" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        </Box>

        {invoicesError && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "#FFF3E0",
              color: "#E65100",
              mb: 2,
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              {t('failedToLoadInvoices')}
            </Typography>
            <Typography variant="body2">
              {t('tryRefreshing')}
            </Typography>
          </Box>
        )}

        {invoicesLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 160,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : filteredInvoices.length === 0 ? (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: "#f5f5f5",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('noInvoicesFound')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {filteredInvoices.map((invoice) => {
              const planName =
                invoice?.metadata?.planName || invoice?.metadata?.planId || "";
              const displayPlanName = planName
                ? `${planName} package`
                : "package";
              const amount = invoice?.amount || 0;
              const currency = (invoice?.currency || "usd").toUpperCase();
              const status = invoice?.status || "";
              const statusLabel =
                status.toLowerCase() === "success" ? "paid" : status;
              const paidOn = invoice?.paidOn || invoice?.createdAt;

              return (
                <ListItem
                  key={invoice.transactionId}
                  sx={{
                    px: 0,
                    py: 1.5,
                    borderBottom: "1px solid #e0e0e0",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ textTransform: "lowercase" }}
                    >
                      {displayPlanName}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textTransform: "lowercase" }}
                    >
                      {currency} {amount}
                    </Typography>
                    {/* transaction id intentionally hidden */}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Chip
                      label={statusLabel}
                      size="small"
                      sx={{
                        backgroundColor:
                          statusLabel.toLowerCase() === "paid"
                            ? "#E8F5E8"
                            : "#FFF3E0",
                        color:
                          statusLabel.toLowerCase() === "paid"
                            ? "#2E7D32"
                            : "#E65100",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                        textTransform: "lowercase",
                      }}
                    />
                    {paidOn && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 0.5, color: "text.secondary" }}
                      >
                        {formatDateTime(paidOn)}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      minWidth: "fit-content",
                      justifyContent: "flex-end",
                    }}
                  >
                    {invoice?.invoicePdfUrl && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownloadInvoice(invoice.invoicePdfUrl)
                        }
                        sx={{
                          color: "#666",
                          "&:hover": {
                            color: "#0085FF",
                            backgroundColor: "rgba(0, 133, 255, 0.1)",
                          },
                        }}
                        aria-label="download invoice"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    )}
                    {status.toLowerCase() === "failed" &&
                    invoice?.hostedInvoiceUrl ? (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() =>
                          handlePayInvoice(invoice.hostedInvoiceUrl)
                        }
                        sx={{
                          backgroundColor: "#0085FF",
                          color: "white",
                          fontWeight: 700,
                          fontSize: "14px",
                          borderRadius: 2,
                          px: 2,
                          py: 0.5,
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#0066CC",
                          },
                        }}
                      >
                        {t('payButton')}
                      </Button>
                    ) : (
                      <Box
                        sx={{
                          width: "60px",
                          height: "32px",
                        }}
                      />
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

const SubscriptionPlanContent = ({
  businessId,
  currentPlanName,
  IsMaxView = false,
  forceShowPlanSelection = false,
  onForceShowPlanSelectionUsed,
  isDowngradeMode = false,
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const tPlanName = (name) => name ? t(`plan_${name.toLowerCase().replace(/ /g, '_')}`, { defaultValue: name }) : name;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.auth.user);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
  const [pendingDowngradePlan, setPendingDowngradePlan] = useState(null);
  const [agreementDialogOpen, setAgreementDialogOpen] = useState(false);
  const popupRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const isClosingRef = useRef(false);

  const hasExistingPlan = React.useMemo(() => {
    if (!currentPlanName) {
      return false;
    }

    if (typeof currentPlanName === "string") {
      const normalizedStatus = currentPlanName.toLowerCase();
      return (
        normalizedStatus !== "inactive" &&
        normalizedStatus !== "none" &&
        normalizedStatus !== "no_plan" &&
        normalizedStatus.trim() !== ""
      );
    }

    if (typeof currentPlanName === "object") {
      const plan = currentPlanName || {};
      return Boolean(
        plan?.planId || plan?._id || plan?.subscriptionPlanId || plan?.planName
      );
    }

    return false;
  }, [currentPlanName]);

  const shouldFetchSubscriptionInfo = !!businessId && hasExistingPlan;

  // Fetch subscription info if businessId is provided
  const {
    data: subscriptionInfoData,
    isLoading: isLoadingInfo,
    error: errorInfo,
  } = useSubscriptionInfo(businessId, shouldFetchSubscriptionInfo);

  // fetch subscription plans using react-query (always call hooks at top level)
  const {
    data: subscriptionData,
    isLoading,
    error,
  } = useSubscriptionPlans(true);

  // fetch subscription invoices
  const {
    data: subscriptionInvoicesData,
    isLoading: isInvoicesLoading,
    error: invoicesError,
  } = useSubscriptionInvoices(businessId, !!businessId);

  // Cleanup function to close popup and clear intervals
  // Note: This function is defined before the mutation, so we can't call mutation.reset() here
  // Instead, we'll reset the mutation in the places where we call closePopup
  const closePopup = (shouldResetMutation = false) => {
    // Prevent multiple close attempts
    if (isClosingRef.current) {
      console.log("Popup already closing, skipping");
      return;
    }

    isClosingRef.current = true;
    console.log("closePopup called", {
      popupExists: !!popupRef.current,
      popupClosed: popupRef.current?.closed,
    });

    // Clear interval first
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }

    // Close the popup
    if (popupRef.current) {
      if (popupRef.current.closed) {
        console.log("Popup already closed");
        popupRef.current = null;
        isClosingRef.current = false;
        return;
      }

      try {
        console.log("Calling popup.close()...");
        popupRef.current.close();

        // Verify closure after a brief delay
        setTimeout(() => {
          if (popupRef.current) {
            if (popupRef.current.closed) {
              console.log("Popup closed  ");
            } else {
              console.warn("Popup may not have closed - trying again");
              try {
                popupRef.current.close();
              } catch (e) {
                console.error("Second close attempt failed:", e);
              }
            }
            popupRef.current = null;
          }
          isClosingRef.current = false;
        }, 200);
      } catch (error) {
        console.error("Error closing popup:", error);
        popupRef.current = null;
        isClosingRef.current = false;
      }
    } else {
      isClosingRef.current = false;
    }
  };

  // Function to handle payment cancellation or failure
  const handlePaymentCancel = (
    errorMessage = t('paymentCancelledOrFailed')
  ) => {
    // Get current businessId from Redux state or props
    const currentBusinessId =
      store.getState()?.auth?.showSubscriptionPlanDialog?.businessId ||
      businessId;

    // Store businessId for retry
    if (currentBusinessId) {
      dispatch(setLastSubscriptionBusinessId(currentBusinessId));
    }

    // Close subscription dialog first
    dispatch(setShowSubscriptionPlanDialog({ show: false, businessId: null }));

    // Set error message and show error dialog after a small delay
    dispatch(setPaymentErrorMessage(errorMessage));
    setTimeout(() => {
      dispatch(setShowPaymentErrorDialog(true));
    }, 100);
  };

  // Set up message listener to close popup on success/cancel
  useEffect(() => {
    const handleMessage = (event) => {
      console.log("Message received:", {
        origin: event.origin,
        data: event.data,
        expectedOrigin: window.location.origin,
      });

      // Verify origin for security
      if (event.origin !== window.location.origin) {
        console.log("Origin mismatch, ignoring message");
        return;
      }

      // Check if message is from success or cancel page
      if (
        event.data === "subscription-success" ||
        event.data === "subscription-cancel"
      ) {
        console.log("Closing popup via postMessage");
        closePopup();
        // Reset mutation state when popup closes via postMessage
        purchaseSubscriptionMutation.reset();

        if (event.data === "subscription-success") {
          // Refetch user details to get updated subscription information
          const refetchUserData = async () => {
            try {
              const storedToken = window.localStorage.getItem(
                authConfig.storageTokenKeyName
              );
              if (storedToken) {
                const result = await dispatch(fetchUser({ storedToken }));

                // Automatically switch to the business after successful purchase
                if (
                  result.type === "auth/fetchUser/fulfilled" &&
                  businessId &&
                  result.payload
                ) {
                  const updatedUser = result.payload;
                  // Find the business that matches the businessId
                  const purchasedBusiness = updatedUser?.business?.find(
                    (b) => b._id === businessId
                  );

                  if (purchasedBusiness) {
                    // Check if subscription is now active or trial before switching
                    if (
                      purchasedBusiness.subscriptionStatus === "active" ||
                      purchasedBusiness.subscriptionStatus === "trial"
                    ) {
                      // Automatically switch to the business profile
                      dispatch(
                        switchProfile({
                          type: "business",
                          businessId: purchasedBusiness._id,
                          primaryLocationId:
                            purchasedBusiness.primaryLocationId,
                        })
                      );
                    }
                  }
                }
              }
            } catch (error) {
              console.error("Error refetching user data:", error);
            }
          };

          // Refetch user data
          refetchUserData();

          // Invalidate React Query caches related to subscription and user data
          queryClient.invalidateQueries(["subscription-info", businessId]);
          queryClient.invalidateQueries(["subscription-invoices", businessId]);
          queryClient.invalidateQueries(["subscription-plans"]);
          queryClient.invalidateQueries(["userProfile"]);
          queryClient.invalidateQueries(["profile-user-info"]);

          // Close subscription dialog first
          dispatch(
            setShowSubscriptionPlanDialog({ show: false, businessId: null })
          );

          // Show payment success dialog after a small delay to ensure subscription dialog closes first
          setTimeout(() => {
            dispatch(setShowPaymentSuccessDialog(true));
          }, 100);

          toast.success("Subscription completed  !");
        } else if (event.data === "subscription-cancel") {
          // Handle payment cancellation
          handlePaymentCancel();
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      // Only remove event listener, don't close popup here
      // Closing popup in cleanup can cause issues when component unmounts for other reasons
      window.removeEventListener("message", handleMessage);
      // Clear interval if it exists
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, []);

  // mutation for creating checkout session and redirecting to Stripe
  const purchaseSubscriptionMutation = useMutation({
    mutationFn: async ({ planId, businessId }) => {
      // Prevent duplicate calls - if popup is already open, don't make another call
      if (popupRef.current && !popupRef.current.closed) {
        console.log("Popup already open, skipping duplicate API call");
        throw new Error("Payment popup is already open");
      }

      // Generate success and cancel URLs
      const successUrl = `${window.location.origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/subscription/cancel`;

      const response = await axiosInstance.post(
        ApiEndPoints.BUSINESS.createCheckoutSession,
        {
          type: "subscription",
          planId,
          businessId,
          successUrl,
          cancelUrl,
        }
      );
      return response.data;
    },
    retry: false, // Don't retry on failure
    onSuccess: (data) => {
      // Check if this is a downgrade response
      if (data?.data?.isDowngrade) {
        // Handle downgrade - no checkout URL, just show success message
        toast.success(data?.message || t('downgradeScheduled'));

        // Refetch user data and invalidate queries
        const refetchUserData = async () => {
          try {
            const storedToken = window.localStorage.getItem(
              authConfig.storageTokenKeyName
            );
            if (storedToken) {
              await dispatch(fetchUser({ storedToken }));
            }
          } catch (error) {
            console.error("Error refetching user data:", error);
          }
        };

        refetchUserData();
        queryClient.invalidateQueries(["subscription-info", businessId]);
        queryClient.invalidateQueries(["subscription-invoices", businessId]);
        queryClient.invalidateQueries(["subscription-plans"]);

        return;
      }

      // Get checkout URL from response (for upgrades/new subscriptions)
      const checkoutUrl = data?.data?.checkoutUrl;

      if (checkoutUrl) {
        // Calculate center position for popup window
        const width = 600;
        const height = 800;

        // Get the current window's position and size
        const windowLeft = window.screenLeft || window.screenX || 0;
        const windowTop = window.screenTop || window.screenY || 0;
        const windowWidth = window.outerWidth || window.innerWidth;
        const windowHeight = window.outerHeight || window.innerHeight;

        // Calculate center position relative to the current window viewport
        const left = windowLeft + (windowWidth - width) / 2;
        const top = windowTop + (windowHeight - height) / 2;

        // Open popup centered on current window
        popupRef.current = window.open(
          checkoutUrl,
          "popup",
          `width=${width},height=${height},left=${Math.round(
            left
          )},top=${Math.round(top)},scrollbars=yes,resizable=yes`
        );

        // Poll to check if popup is closed manually or navigated away
        checkIntervalRef.current = setInterval(() => {
          // Skip if already closing
          if (isClosingRef.current) {
            return;
          }

          if (!popupRef.current) {
            if (checkIntervalRef.current) {
              clearInterval(checkIntervalRef.current);
              checkIntervalRef.current = null;
            }
            return;
          }

          // Check if popup was manually closed
          if (popupRef.current.closed) {
            console.log("Popup was manually closed");
            closePopup();
            // Reset mutation state when popup is manually closed
            purchaseSubscriptionMutation.reset();
            // Handle as cancellation
            handlePaymentCancel(t('paymentCancelled'));
            return;
          }

          // Try to access popup location (only works if same origin)
          try {
            const popupLocation = popupRef.current.location;
            const href = popupLocation.href;
            const pathname = popupLocation.pathname;

            console.log("Checking popup location:", { href, pathname });

            // Check if popup has navigated to success or cancel page
            if (
              pathname === "/subscription/success" ||
              pathname === "/subscription/cancel" ||
              href.includes("/subscription/success") ||
              href.includes("/subscription/cancel")
            ) {
              console.log(
                "Detected navigation to success/cancel page, closing popup"
              );

              if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
              }

              // If success page, refetch user data and show success dialog
              if (
                pathname === "/subscription/success" ||
                href.includes("/subscription/success")
              ) {
                // Refetch user details to get updated subscription information
                const refetchUserData = async () => {
                  try {
                    const storedToken = window.localStorage.getItem(
                      authConfig.storageTokenKeyName
                    );
                    if (storedToken) {
                      const result = await dispatch(fetchUser({ storedToken }));

                      // Automatically switch to the business after successful purchase
                      if (
                        result.type === "auth/fetchUser/fulfilled" &&
                        businessId &&
                        result.payload
                      ) {
                        const updatedUser = result.payload;
                        // Find the business that matches the businessId
                        const purchasedBusiness = updatedUser?.business?.find(
                          (b) => b._id === businessId
                        );

                        if (purchasedBusiness) {
                          // Check if subscription is now active or trial before switching
                          if (
                            purchasedBusiness.subscriptionStatus === "active" ||
                            purchasedBusiness.subscriptionStatus === "trial"
                          ) {
                            // Automatically switch to the business profile
                            dispatch(
                              switchProfile({
                                type: "business",
                                businessId: purchasedBusiness._id,
                                primaryLocationId:
                                  purchasedBusiness.primaryLocationId,
                              })
                            );
                          }
                        }
                      }
                    }
                  } catch (error) {
                    console.error("Error refetching user data:", error);
                  }
                };

                // Refetch user data
                refetchUserData();

                // Invalidate React Query caches related to subscription and user data
                queryClient.invalidateQueries([
                  "subscription-info",
                  businessId,
                ]);
                queryClient.invalidateQueries([
                  "subscription-invoices",
                  businessId,
                ]);
                queryClient.invalidateQueries(["subscription-plans"]);
                queryClient.invalidateQueries(["userProfile"]);
                queryClient.invalidateQueries(["profile-user-info"]);

                // Close subscription dialog first
                dispatch(
                  setShowSubscriptionPlanDialog({
                    show: false,
                    businessId: null,
                  })
                );

                // Show payment success dialog after a small delay to ensure subscription dialog closes first
                setTimeout(() => {
                  dispatch(setShowPaymentSuccessDialog(true));
                }, 100);
              } else if (
                pathname === "/subscription/cancel" ||
                href.includes("/subscription/cancel")
              ) {
                // Handle payment cancellation
                handlePaymentCancel();
              }

              // Close the popup - use closePopup function
              closePopup();
              // Reset mutation state when popup navigates to success/cancel page
              purchaseSubscriptionMutation.reset();
              return;
            }
          } catch (e) {
            // Cross-origin error is expected when Stripe redirects
            // The message listener will handle closing the popup
            // Don't log this as it's expected behavior
          }
        }, 500);
      } else {
        toast.error(t('failedToGetCheckoutUrl'));
        console.error("No checkout URL in response:", data);
      }
    },
    onError: (error) => {
      console.error("checkout session creation failed:", error);

      if (error?.message === "Payment popup is already open") {
        return;
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "payment gateway error. please try again.";

      // Close subscription dialog and show error dialog
      handlePaymentCancel(errorMessage);

      // Reset mutation state - this is important to prevent issues
      // Don't clear user data - errors should not affect authentication
    },
  });

  // Get data after hooks
  const subscriptionInfo = subscriptionInfoData?.data?.data;
  const subscriptionStatus =
    subscriptionInfo?.subscriptionStatus || currentPlanName;
  const plans = subscriptionData?.data?.data || [];
  const invoices = subscriptionInvoicesData?.data?.invoices || [];
  const isScheduledDowngrade = subscriptionInfo?.isScheduledDowngrade || false;
  const scheduledPlanName = subscriptionInfo?.scheduledPlanName || "";
  const scheduledDowngradeDate = subscriptionInfo?.scheduledDowngradeDate;

  // Get current plan name from subscriptionInfo if available, otherwise use currentPlanName prop
  // Ensure it's a string (handle case where subscriptionPlan might be passed as object)
  const effectiveCurrentPlanName =
    typeof subscriptionInfo?.subscriptionPlan === "string"
      ? subscriptionInfo.subscriptionPlan
      : subscriptionInfo?.subscriptionPlan?.planName ||
        (typeof currentPlanName === "string"
          ? currentPlanName
          : typeof currentPlanName === "object" && currentPlanName?.planName
          ? currentPlanName.planName
          : null);

  // Filter plans - in downgrade mode show all plans, otherwise show all plans
  const filteredPlans = plans;

  // Set selected plan index based on mode
  React.useEffect(() => {
    if (filteredPlans.length === 0) return;

    if (isDowngradeMode && subscriptionInfo?.subscriptionPlan) {
      // In downgrade mode, select the first lower-priced plan
      const currentPrice = subscriptionInfo.subscriptionPlan.pricePerMonth;
      const lowerPlanIndex = filteredPlans.findIndex(
        (plan) => plan.pricePerMonth < currentPrice
      );
      if (lowerPlanIndex !== -1) {
        setSelectedPlanIndex(lowerPlanIndex);
      }
    } else if (effectiveCurrentPlanName) {
      // Ensure effectiveCurrentPlanName is a string
      const planName =
        typeof effectiveCurrentPlanName === "string"
          ? effectiveCurrentPlanName
          : typeof effectiveCurrentPlanName === "object" &&
            effectiveCurrentPlanName?.planName
          ? effectiveCurrentPlanName.planName
          : null;

      if (planName) {
        const currentPlanIndex = filteredPlans.findIndex(
          (plan) => plan.planName.toLowerCase() === planName.toLowerCase()
        );
        if (currentPlanIndex !== -1) {
          const statusLower = (subscriptionStatus || "").toLowerCase();
          const isExpiredStatus = statusLower === "expired" || statusLower === "suspended";
          setSelectedPlanIndex(
            isExpiredStatus
              ? currentPlanIndex
              : currentPlanIndex < filteredPlans.length - 1
              ? currentPlanIndex + 1
              : currentPlanIndex
          );
        }
      }
    }
  }, [
    filteredPlans,
    effectiveCurrentPlanName,
    isDowngradeMode,
    subscriptionStatus,
    subscriptionInfo,
  ]);

  // Handle forceShowPlanSelection prop
  React.useEffect(() => {
    if (forceShowPlanSelection) {
      setShowPlanSelection(true);
      // Notify parent that we've used the prop so it can be reset
      if (onForceShowPlanSelectionUsed) {
        onForceShowPlanSelectionUsed();
      }
    }
  }, [forceShowPlanSelection, onForceShowPlanSelectionUsed]);

  // Use filtered plans for selected plan (calculate before early returns)
  const validSelectedPlanIndex = Math.min(
    selectedPlanIndex,
    filteredPlans.length - 1
  );
  const selectedPlan = filteredPlans[validSelectedPlanIndex];

  const getDisplayFeatures = (plan) => {
    if (!plan?.features) return [];

    const enabledFeatures = plan.features.filter((feature) => feature.enabled);

    const sortedFeatures = enabledFeatures.sort(
      (a, b) => a.displayOrder - b.displayOrder
    );

    return sortedFeatures.map((feature) => {
      const translationKey = `feat_${feature.featureKey}`;
      const translated = t(translationKey, { defaultValue: '' });
      const formatted = translated || formatFeatureKey(feature.featureKey);
      const displayName = feature.comingSoon
        ? `${formatted} (${t('comingSoon', { defaultValue: 'coming soon' })})`
        : formatted;
      return { ...feature, displayName: displayName.toLowerCase() };
    });
  };

  const getPlanActionType = (selectedPlan) => {
    if (
      !subscriptionInfo?.subscriptionPlan ||
      !selectedPlan ||
      (normalizedSubscriptionStatus !== "active" &&
        normalizedSubscriptionStatus !== "trial" &&
        normalizedSubscriptionStatus !== "suspended")
    ) {
      return null;
    }

    const currentPrice = subscriptionInfo.subscriptionPlan.pricePerMonth;
    const selectedPrice = selectedPlan.pricePerMonth;

    if (selectedPrice > currentPrice) {
      return "upgrade";
    } else if (selectedPrice < currentPrice) {
      return "downgrade";
    } else {
      return "same";
    }
  };

  const proceedWithPlanPurchase = (plan) => {
    let selectedBusinessId = businessId;

    if (selectedBusinessId) {
      dispatch(setLastSubscriptionBusinessId(selectedBusinessId));
    }

    if (!selectedBusinessId) {
      const firstBusiness = user?.business?.[0];
      if (firstBusiness?._id) {
        selectedBusinessId = firstBusiness._id;
      } else {
        toast.error(t('businessIdNotFound'));
        return;
      }
    }

    purchaseSubscriptionMutation.mutate({
      planId: plan._id,
      businessId: selectedBusinessId,
    });
  };

  const handlePlanSelect = (plan) => {
    if (purchaseSubscriptionMutation.isPending) {
      return;
    }

    if (popupRef.current && !popupRef.current.closed) {
      return;
    }

    const actionType = getPlanActionType(plan);
    if (actionType === "downgrade") {
      setPendingDowngradePlan(plan);
      setDowngradeDialogOpen(true);
      return;
    }

    proceedWithPlanPurchase(plan);
  };

  const handleDowngradeConfirm = () => {
    if (pendingDowngradePlan) {
      setDowngradeDialogOpen(false);
      proceedWithPlanPurchase(pendingDowngradePlan);
      setPendingDowngradePlan(null);
    }
  };

  const handleDowngradeDialogClose = () => {
    setDowngradeDialogOpen(false);
    setPendingDowngradePlan(null);
  };

  const handlePlanSwitch = (planIndex) => {
    setSelectedPlanIndex(planIndex);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedPlanIndex(newValue);
  };

  const normalizedSubscriptionStatus =
    typeof subscriptionStatus === "string"
      ? subscriptionStatus.toLowerCase()
      : "";

  const shouldShowManagement =
    !showPlanSelection &&
    (normalizedSubscriptionStatus === "active" ||
      normalizedSubscriptionStatus === "trial" ||
      normalizedSubscriptionStatus === "suspended") &&
    subscriptionInfo;

  if (shouldShowManagement) {
    if (isLoadingInfo) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <SubscriptionManagementView
        subscriptionInfo={subscriptionInfo}
        invoices={invoices}
        invoicesLoading={isInvoicesLoading}
        invoicesError={invoicesError}
        onUpgrade={() => setShowPlanSelection(true)}
        onSwitchPackage={() => setShowPlanSelection(true)}
        IsMaxView={IsMaxView}
        businessId={businessId}
        subscriptionStatus={subscriptionStatus}
      />
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography color="error" align="center">
          {t('errorLoadingPlans')}
        </Typography>
      </Box>
    );
  }

  const getCurrentAndNextPlan = () => {
    const planName =
      typeof effectiveCurrentPlanName === "string"
        ? effectiveCurrentPlanName
        : typeof effectiveCurrentPlanName === "object" &&
          effectiveCurrentPlanName?.planName
        ? effectiveCurrentPlanName.planName
        : null;

    if (!planName || plans.length === 0) {
      return { currentPlan: null, nextPlan: null, isHighestPlan: false };
    }

    const currentPlanIndex = plans.findIndex(
      (plan) => plan.planName.toLowerCase() === planName.toLowerCase()
    );

    if (currentPlanIndex === -1) {
      return { currentPlan: null, nextPlan: null, isHighestPlan: false };
    }

    const currentPlan = plans[currentPlanIndex];
    const nextPlan =
      currentPlanIndex < plans.length - 1 ? plans[currentPlanIndex + 1] : null;
    const isHighestPlan = currentPlanIndex === plans.length - 1;

    return { currentPlan, nextPlan, isHighestPlan };
  };

  const { currentPlan, nextPlan, isHighestPlan } = getCurrentAndNextPlan();

  return (
    <>
      {/*
        ─── LAYOUT STRATEGY ─────────────────────────────────────────────────────
        The outer Box is the full dialog height container (80vh when in dialog).
        We split it into two flex children:
          1. compactHeader  – flexShrink: 0, never grows, never shrinks
          2. planCard       – flex: 1, min-height: 0, owns all remaining space
        Inside planCard the MUI Card also stretches to 100% and its CardContent
        uses an inner flex column so the features list can scroll freely without
        collapsing to almost nothing.
        ─────────────────────────────────────────────────────────────────────────
      */}
      <Box
        sx={{
          width: "100%",
          maxWidth: IsMaxView ? "100%" : "600px",
          mx: "auto",
          // Use padding only in MaxView; in dialog let the dialog control height
          p: IsMaxView ? 2 : 0,
          height: IsMaxView ? "auto" : "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* ── COMPACT HEADER (fixed height, never scrolls) ── */}
        <Box
          sx={{
            flexShrink: 0,
            textAlign: "center",
            pt: 4,
            pb: 1,
            backgroundColor: "white",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          {/* Logo left, name + tagline right */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
              justifyContent: "center",
            }}
          >
            <Avatar
              src="/logo.svg"
              sx={{
                width: 70,
                height: 70,
                bgcolor: "#fff",
                border: "5px solid #A259FF",
                flexShrink: 0,
                padding: 3,
              }}
            >
              <NameLogo />
            </Avatar>
            <Box sx={{ textAlign: "left" }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                letterSpacing={0.5}
                sx={{
                  textTransform: "lowercase",
                  textAlign: "center",
                  color: "#000",
                  fontFamily: "Inter, Arial, sans-serif",
                  lineHeight: 1.1,
                  fontSize: { xs: "1.5rem", sm: "2.5rem" },
                }}
              >
                be
                <Box component="span" sx={{ color: "#A259FF" }}>
                  ɘ
                </Box>
                mz
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                {t('tagline')}
              </Typography>
            </Box>
          </Box>

          {/* Status / description – compact one-liners */}
          {isScheduledDowngrade && scheduledPlanName ? (
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                backgroundColor: "#FFF3E0",
                border: "1px solid #FFB74D",
                mb: 0.5,
              }}
            >
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", color: "#E65100", display: "block" }}
            >
              {t('planDowngradeScheduled')}
            </Typography>
              <Typography variant="body2" sx={{ color: "#E65100" }}>
                {t('switchingFrom', {
                  fromPlan: tPlanName(subscriptionInfo?.subscriptionPlan?.planName || currentPlan?.planName),
                  toPlan: tPlanName(scheduledPlanName),
                  date: scheduledDowngradeDate ? formatDate(scheduledDowngradeDate) : '',
                })}
              </Typography>
            </Box>
          ) : (normalizedSubscriptionStatus === "expired" ||
              normalizedSubscriptionStatus === "suspended") ? (
            <Typography variant="body2" color="error" sx={{ fontWeight: "bold", display: "block", mb: 0.5 }}>
              {t('autoRenewalFailed')}
              {currentPlan && ` — ${t('currentPlanLabel', { planName: tPlanName(currentPlan.planName) })}`}
            </Typography>
          ) : currentPlan ? (
            <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              {t('currentPlanLabel', { planName: tPlanName(currentPlan.planName) })}
              {isHighestPlan
                ? ` · ${t('youreOnHighestPlan')}`
                : nextPlan
                ? ` · ${t('upgradeForMoreFeatures', { planName: tPlanName(nextPlan.planName) })}`
                : ""}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              {t('subscribeToBeemz')}
            </Typography>
          )}
        </Box>

        {/* ── PLAN CARD (takes all remaining height) ── */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,          // critical – lets flex child shrink below content size
            display: "flex",
            flexDirection: "column",
            px: 1.5,
            py: 1,
            backgroundColor: "white",
          }}
        >
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              flex: 1,
              minHeight: 0,         // critical
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <CardContent
              sx={{
                p: 0,
                flex: 1,
                minHeight: 0,       // critical
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                // Remove MUI's forced bottom padding
                "&:last-child": { pb: 0 },
              }}
            >
              {/* Plan tabs – sticky at the top of the card */}
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: "divider",
                  flexShrink: 0,
                  backgroundColor: "#f5f5f5",
                }}
              >
                <Tabs
                  value={validSelectedPlanIndex}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 40,
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                      minWidth: "auto",
                      px: 2,
                      py: 1,
                      minHeight: 40,
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#e3f2fd",
                      color: "#6200ea",
                      borderBottom: "3px solid #6200ea",
                    },
                  }}
                >
                  {filteredPlans.map((plan, index) => (
                    <Tab
                      key={plan._id}
                      label={t(`plan_${plan.planName.toLowerCase().replace(/ /g, '_')}`, { defaultValue: plan.planName })}
                      sx={{
                        textTransform: "capitalize",
                        color:
                          validSelectedPlanIndex === index ? "#6200ea" : "#666",
                      }}
                    />
                  ))}
                </Tabs>
              </Box>

              {/* Plan details – this inner box handles all scrolling */}
              {selectedPlan && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,     // critical
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    px: 2,
                    pt: 1.5,
                    pb: 1,
                  }}
                >
                  {/* Price */}
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ flexShrink: 0, mb: 0.5 }}
                  >
                    ${convertNumerals(selectedPlan.pricePerMonth, i18n.language)}/{t('month')}
                  </Typography>

                  {/* Features list – the ONLY scrollable region */}
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,   // critical
                      overflowY: "auto",
                      mb: 1,
                      "&::-webkit-scrollbar": { width: "6px" },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#c1c1c1",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        background: "#a8a8a8",
                      },
                    }}
                  >
                    <List disablePadding>
                      {getDisplayFeatures(selectedPlan).map((feature) => (
                        <ListItem
                          key={feature._id}
                          sx={{ px: 0, py: 0.25, alignItems: "center" }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 32,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {!feature.isFree && (
                              <CheckIcon sx={{ color: "#4caf50", fontSize: 18 }} />
                            )}
                            {feature.isFree && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#4caf50",
                                  fontWeight: "bold",
                                  fontSize: "0.7rem",
                                }}
                              >
                                {t('freeLabel')}
                              </Typography>
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                                {!feature.isFree && (
                                  <>
                                    {convertNumerals(feature.quantity, i18n.language)} {feature.displayName}
                                  </>
                                )}
                                {feature.isFree && feature.displayName}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* T&C link – fixed above buttons, never scrolls away */}
                  <Box textAlign="center" sx={{ flexShrink: 0, mb: 1 }}>
                    <Link
                      component="button"
                      onClick={() => setAgreementDialogOpen(true)}
                      sx={{
                        color: "#6200EE",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        textTransform: "lowercase",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {t('agreeToTerms')}
                    </Link>
                  </Box>

                  {/* Action buttons – always visible at the bottom */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      flexShrink: 0,
                    }}
                  >
                    {/* Current / select plan button */}
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      onClick={() => handlePlanSelect(selectedPlan)}
                      disabled={
                        purchaseSubscriptionMutation.isPending ||
                        isScheduledDowngrade ||
                        (normalizedSubscriptionStatus === "suspended" &&
                          currentPlan &&
                          selectedPlan.planName.toLowerCase() !==
                            currentPlan.planName.toLowerCase()) ||
                        (currentPlan &&
                          selectedPlan.planName.toLowerCase() ===
                            currentPlan.planName.toLowerCase() &&
                          (normalizedSubscriptionStatus === "active" ||
                            normalizedSubscriptionStatus === "trial"))
                      }
                      sx={{
                        backgroundColor:
                          currentPlan &&
                          selectedPlan.planName.toLowerCase() ===
                            currentPlan.planName.toLowerCase() &&
                          (normalizedSubscriptionStatus === "active" ||
                            normalizedSubscriptionStatus === "trial")
                            ? "#E8F5E8"
                            : "#FFFFFF",
                        borderColor:
                          currentPlan &&
                          selectedPlan.planName.toLowerCase() ===
                            currentPlan.planName.toLowerCase() &&
                          (normalizedSubscriptionStatus === "active" ||
                            normalizedSubscriptionStatus === "trial")
                            ? "#4CAF50"
                            : "#6200EE",
                        color:
                          currentPlan &&
                          selectedPlan.planName.toLowerCase() ===
                            currentPlan.planName.toLowerCase() &&
                          (normalizedSubscriptionStatus === "active" ||
                            normalizedSubscriptionStatus === "trial")
                            ? "#2E7D32"
                            : "#C9A4FF",
                        borderRadius: "8px",
                        py: 0.75,
                        textTransform: "none",
                        fontSize: "0.78rem",
                        fontWeight: "bold",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        "&:hover": {
                          backgroundColor:
                            currentPlan &&
                            selectedPlan.planName.toLowerCase() ===
                              currentPlan.planName.toLowerCase() &&
                            (normalizedSubscriptionStatus === "active" ||
                              normalizedSubscriptionStatus === "trial")
                              ? "#E8F5E8"
                              : "#F8F9FA",
                          borderColor:
                            currentPlan &&
                            selectedPlan.planName.toLowerCase() ===
                              currentPlan.planName.toLowerCase() &&
                            (normalizedSubscriptionStatus === "active" ||
                              normalizedSubscriptionStatus === "trial")
                              ? "#4CAF50"
                              : "#6200EE",
                          color:
                            currentPlan &&
                            selectedPlan.planName.toLowerCase() ===
                              currentPlan.planName.toLowerCase() &&
                            (normalizedSubscriptionStatus === "active" ||
                              normalizedSubscriptionStatus === "trial")
                              ? "#2E7D32"
                              : "#C9A4FF",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                        },
                        "&:disabled": {
                          backgroundColor: "#F5F5F5",
                          borderColor: "#E0E0E0",
                          color: "#BDBDBD",
                        },
                      }}
                    >
                      {purchaseSubscriptionMutation.isPending ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CircularProgress size={14} color="inherit" />
                          {t('processing')}
                        </Box>
                      ) : (normalizedSubscriptionStatus === "expired" ||
                          normalizedSubscriptionStatus === "suspended") &&
                        currentPlan &&
                        selectedPlan.planName.toLowerCase() ===
                          currentPlan.planName.toLowerCase() ? (
                        t('reactivatePlan', { planName: tPlanName(selectedPlan.planName) })
                      ) : (
                        (() => {
                          const actionType = getPlanActionType(selectedPlan);
                          if (
                            actionType === "same" &&
                            (normalizedSubscriptionStatus === "active" ||
                              normalizedSubscriptionStatus === "trial")
                          ) {
                            return t('currentPlanLabel', { planName: tPlanName(selectedPlan.planName) });
                          } else if (actionType === "upgrade") {
                            return t('upgradeToPlan', { planName: tPlanName(selectedPlan.planName) });
                          } else if (actionType === "downgrade") {
                            return t('downgradeToPlan', { planName: tPlanName(selectedPlan.planName) });
                          } else {
                            return t('getPlan', { planName: tPlanName(selectedPlan.planName) });
                          }
                        })()
                      )}
                    </Button>

                    {/* Next plan button */}
                    {validSelectedPlanIndex < filteredPlans.length - 1 && (() => {
                      const nextPlan = filteredPlans[validSelectedPlanIndex + 1];
                      const nextActionType = getPlanActionType(nextPlan);
                      const nextPlanLabel =
                        nextActionType === "upgrade"
                          ? t('upgradeToPlan', { planName: tPlanName(nextPlan.planName) })
                          : nextActionType === "downgrade"
                            ? t('downgradeToPlan', { planName: tPlanName(nextPlan.planName) })
                            : nextActionType === "same" &&
                                (normalizedSubscriptionStatus === "active" ||
                                  normalizedSubscriptionStatus === "trial")
                              ? t('currentPlanLabel', { planName: tPlanName(nextPlan.planName) })
                              : t('getPlan', { planName: tPlanName(nextPlan.planName) });
                      return (
                      <Button
                        variant="contained"
                        fullWidth
                        size="small"
                        onClick={() =>
                          handlePlanSwitch(validSelectedPlanIndex + 1)
                        }
                        disabled={
                          purchaseSubscriptionMutation.isPending ||
                          normalizedSubscriptionStatus === "suspended" ||
                          normalizedSubscriptionStatus === "expired" ||
                          isScheduledDowngrade
                        }
                        sx={{
                          backgroundColor: "#9347FF",
                          color: "#FFFFFF",
                          borderRadius: "8px",
                          py: 0.75,
                          textTransform: "none",
                          fontSize: "0.78rem",
                          fontWeight: "bold",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          "&:hover": {
                            backgroundColor: "primary.main",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                          },
                          "&:disabled": {
                            backgroundColor: "#E0E0E0",
                            color: "#BDBDBD",
                          },
                        }}
                      >
                        {nextPlanLabel}
                      </Button>
                    );
                    })()}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Downgrade Confirmation Dialog */}
      <DowngradePlanDialog
        open={downgradeDialogOpen}
        onClose={handleDowngradeDialogClose}
        planName={pendingDowngradePlan?.planName || ""}
        onConfirm={handleDowngradeConfirm}
        isLoading={purchaseSubscriptionMutation.isPending}
      />

      {/* Subscription Agreement Dialog */}
      <SubscriptionAgreementDialog
        open={agreementDialogOpen}
        onClose={() => setAgreementDialogOpen(false)}
      />
    </>
  );
};

export default SubscriptionPlanContent;